import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError, formatDateTime } from "../lib/api";
import StarRating from "../components/StarRating";
import CourseCard from "../components/CourseCard";
import { toast } from "sonner";

function renderInstructorList(loading, list, t) {
  if (loading) {
    return <div className="text-center py-20 text-xs uppercase tracking-[0.3em] text-zinc-500">{t("common.loading")}</div>;
  }
  if (list.length === 0) {
    return <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] mt-8 text-zinc-400">{t("instructors_page.no_instructors")}</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="instructors-grid">
      {list.map((i) => (
        <Link key={i.id} to={`/instructors/${i.id}`} className="border border-white/10 bg-[#0A0A0A] p-6 hover:border-[#E30613]/60 transition-colors block" data-testid={`instructor-card-${i.id}`}>
          <div className="font-display text-2xl uppercase font-black">{i.name}</div>
          <div className="mt-2 flex items-center gap-2">
            <StarRating value={i.rating_avg || 0} testId={`instr-rating-${i.id}`} />
            <span className="text-xs text-zinc-400">{i.rating_avg || "0.0"} · {t("instructors_page.reviews_count", { count: i.rating_count || 0 })}</span>
          </div>
          {i.bio && <p className="mt-3 text-sm text-zinc-400 line-clamp-3">{i.bio}</p>}
          <div className="mt-4 text-[10px] uppercase tracking-widest text-[#E30613] font-bold">{t("instructors_page.view_profile")} →</div>
        </Link>
      ))}
    </div>
  );
}

export default function Instructors() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/instructors")
      .then((r) => setList(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" data-testid="instructors-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("instructors_page.tag")}</div>
        <h1 className="font-display text-5xl uppercase font-black mt-2 tracking-tight">{t("instructors_page.title")}</h1>
        {renderInstructorList(loading, list, t)}
      </div>
    </div>
  );
}

export function InstructorProfile() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [instr, setInstr] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/instructors/${id}`),
      api.get("/courses", { params: { instructor_id: id, status: "approved" } }),
    ])
      .then(([a, b]) => { setInstr(a.data); setCourses(b.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-20 text-xs uppercase tracking-[0.3em] text-zinc-500">{t("common.loading")}</div>;
  if (!instr) return <div className="text-center py-20">{t("instructors_page.no_instructors")}</div>;

  return (
    <div className="min-h-screen" data-testid="instructor-profile-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("course.instructor")}</div>
          <h1 className="font-display text-5xl uppercase font-black mt-2 tracking-tight">{instr.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <StarRating value={instr.rating_avg || 0} size={20} />
            <span className="text-sm text-zinc-300">{instr.rating_avg || "0.0"} · {t("instructors_page.reviews_count", { count: instr.rating_count || 0 })}</span>
          </div>
          {instr.bio && <p className="mt-5 text-zinc-300 leading-relaxed max-w-3xl">{instr.bio}</p>}
        </div>

        <h2 className="font-display text-3xl uppercase font-black mt-10">{t("instructors_page.upcoming_courses")}</h2>
        {courses.length === 0 ? (
          <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] mt-4 text-zinc-400">{t("instructors_page.no_upcoming")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {courses.map((c, i) => <CourseCard key={c.id} course={c} index={i} />)}
          </div>
        )}

        <h2 className="font-display text-3xl uppercase font-black mt-12">{t("instructors_page.reviews_section")}</h2>
        {(instr.reviews || []).length === 0 ? (
          <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] mt-4 text-zinc-400">{t("instructors_page.no_reviews")}</div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="instructor-reviews-list">
            {instr.reviews.map((r) => (
              <div key={r.id} className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <StarRating value={r.rating} />
                    <span className="text-sm text-white font-semibold">{r.client_name}</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">{formatDateTime(r.created_at)}</span>
                </div>
                {r.comment && <p className="mt-3 text-sm text-zinc-300">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
